import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { nextDay, previousDay } from '@/utils/dateRange';
import { FilterPanel } from './FilterPanel';

type DateRangeFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  showTime?: boolean;
};

const toDayjs = (value: string | undefined): Dayjs | null =>
  value ? dayjs(value) : null;

/**
 * Date range column filter.
 *
 * By day it writes `field[gte]` / `field[lt]`, the upper bound exclusive — see
 * `dayRangeFilters` for why an inclusive one drops the last day on a timestamp
 * column. With `showTime` the user picks exact instants, so both ends are
 * theirs to mean literally and it stays `gte` / `lte`.
 */
export const DateRangeFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  showTime = false,
}: DateRangeFilterProps) => {
  const t = useTranslate();
  const currentFrom = getFilterValue<string>(filters, field, 'gte');
  const exclusiveTo = getFilterValue<string>(filters, field, 'lt');
  // `lte` is still read so a link written before the exclusive bound — or by
  // `showTime`, which keeps it — seeds the picker with the day it names.
  const currentTo =
    exclusiveTo != null
      ? previousDay(exclusiveTo)
      : getFilterValue<string>(filters, field, 'lte');
  const [from, setFrom] = useState<Dayjs | null>(toDayjs(currentFrom));
  const [to, setTo] = useState<Dayjs | null>(toDayjs(currentTo));

  useEffect(() => setFrom(toDayjs(currentFrom)), [currentFrom]);
  useEffect(() => setTo(toDayjs(currentTo)), [currentTo]);

  const format = showTime
    ? (v: Dayjs) => v.toISOString()
    : (v: Dayjs) => v.format('YYYY-MM-DD');

  const handleFilter = () => {
    const start = from ? format(from) : undefined;
    const end = to ? format(to) : undefined;
    setFilters(
      showTime
        ? [
            { field, operator: 'gte', value: start },
            { field, operator: 'lte', value: end },
          ]
        : [
            { field, operator: 'gte', value: start },
            { field, operator: 'lt', value: end && nextDay(end) },
            // The two bounds are alternatives; a stale one would narrow the
            // range the user just asked for.
            { field, operator: 'lte', value: undefined },
          ],
      'merge'
    );
    confirm();
  };

  const handleReset = () => {
    setFrom(null);
    setTo(null);
    setFilters(
      [
        { field, operator: 'gte', value: undefined },
        { field, operator: 'lte', value: undefined },
        { field, operator: 'lt', value: undefined },
      ],
      'merge'
    );
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <DatePicker
        value={from}
        onChange={setFrom}
        placeholder={t('base.filter.from', 'Od')}
        style={{ width: 200 }}
        showTime={showTime}
      />
      <DatePicker
        value={to}
        onChange={setTo}
        placeholder={t('base.filter.to', 'Do')}
        style={{ width: 200 }}
        showTime={showTime}
      />
    </FilterPanel>
  );
};
